import { Checkbox } from '../../../../../../widgets/checkbox';
import { TabHeader } from '../../../../../../widgets/tab';
import { useDialogManager } from '../../../../../../widgets/dialog';
import React, { FC } from 'react';
import usePreview3DStore from 'src/components/widgets/preview3D/store';
import { T } from 'src/shared/helpers';
import Edit from 'src/shared/icons/edit-solid.svg';
import Play from 'src/shared/icons/play-solid.svg';
import Plus from 'src/shared/icons/plus-solid.svg';
import Trash from 'src/shared/icons/trash-alt-solid.svg';
import theme from 'src/shared/theme';
import styled from 'styled-components';
import { ComposerActionTreeElement } from '../../../configurator-composer/composer/components/composerActionTreeElement';
import useComposerStore from '../../../configurator-composer/composer/composerStore';
import ComposerSaveActionDialog from '../../../configurator-composer/composer/dialogs/save/composerSaveActionDialog';
import { setActionSceneElementType, sortByDisplay } from '../../../configurator-composer/composer/helpers/composerHelpers';
import { ComposerAttributeNodeAdapter } from '../../../configurator-composer/composer/helpers/composerNodesAdapter';
import { TreeNodeHook } from '../../scene-editors/hooks/useTreeNodeManager';
import { ToolGrid } from '../../toolsgrid';
import { ComposerAttributeNode, ComposerAttributeOptionNode, ComposerEcommercePlatform, ComposerOptionActionNode } from './composerAttributesModels';

const ComposerActionsContainer = styled.div`
    display: flex;
    flex-direction: column;
    width: 38%;
    height: 100%;
    padding: 0 15px;
    padding-top: 0px;
    border-right: 1px solid rgba(0, 0, 0, 0.15);
`;
const ComposerActionsHeader = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    align-items: center;
    padding-top: 24px;
    padding-bottom: 10px;
    border-bottom: 1px solid ${theme.app.borderColor};
    min-height: 160px;
    position: relative;
`;

const ComposerActionsBody = styled.div`
    padding: 0px 0;
    flex: 1;
    overflow-y: auto;
    position: relative;
`;
const ComposerActionsList = styled.div<{ $hasActions: boolean }>`
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 85%;
    overflow-y: auto;
    gap: 8px;
    position: relative;
    padding-top: 35px;

    ${(props) => !props.$hasActions && 'justify-content: center; align-items: center;'}
`;

const ComposerActionHeaders = styled.div`
    display: flex;
    flex-direction: row;
    gap: 8px;
    justify-content: start;
    gap: 50px;
    align-items: center;
    border-bottom: 1px solid ${theme.app.borderColor};
    background: transparent;
    outline: none;
    padding: 10px;
    padding-left: 50px;
    & > div {
        width: 100px;
        text-align: center;
    }
`;

const ComposerActionsSelectorContainer = styled.div`
    position: absolute;
    height: 30px;
    top: 5px;
    left: 0;
    z-index: 10;
    cursor: pointer;
`;
const ComposerActionsSelector: FC<{ selected: boolean; onClick: () => void }> = ({ selected, onClick }) => {
    return (
        <ComposerActionsSelectorContainer onClick={onClick}>
            <Checkbox checked={selected} onChange={onClick} label='Select all items' />
        </ComposerActionsSelectorContainer>
    );
};

const ComposerActions: FC<{
    selectedAttribute: ComposerAttributeNode;
    selectedOption?: ComposerAttributeOptionNode;
    actions: ComposerOptionActionNode[];
    multiple: boolean;
    state: TreeNodeHook<ComposerOptionActionNode>;
    materials?: BABYLON.Material[];
    meshes?: BABYLON.AbstractMesh[];
    cameras?: BABYLON.Camera[];
}> = ({ selectedAttribute, selectedOption, actions, multiple, state, materials, meshes, cameras }) => {
    const [zkScene, sceneEditor, zkProduct] = usePreview3DStore((x) => [x.zkScene, x.sceneEditor, x.zkProduct]);
    const [updateAction, removeAction, ecommerceSettings] = useComposerStore((state) => [state.updateAction, state.removeAction, state.ecommerceSettings]);
    const { showDialog, closeDialog } = useDialogManager();
    const { filter, setFilter, selectedIds, filteredData, addNode, updateNode, findNodeById, removeNodes, treeState, setSelectedIds } = state;

    const saveAction = (passedAction: ComposerOptionActionNode | null) => {
        showDialog(
            'saveAction',
            <ComposerSaveActionDialog
                attributeID={selectedAttribute.id}
                optionID={passedAction?.optionID || ''}
                onConfirm={(action: ComposerOptionActionNode | null) => {
                    if (!action) return;

                    setActionSceneElementType(action);
                    action.attributeID = selectedAttribute.id;
                    action.optionID = selectedOption?.id || '';
                    if (passedAction) {
                        updateNode(passedAction.id, action);
                    } else {
                        addNode(action);
                    }
                    updateAction(action);
                    closeDialog('saveAction');
                }}
                passedAction={passedAction}
                materials={materials}
                meshes={meshes}
                cameras={cameras}
                edit={!!passedAction}
            />
        );
    };

    const playAction = (action: ComposerOptionActionNode) => {
        sceneEditor?.executeActionsAsync(selectedAttribute.idNumeric, null, [ComposerAttributeNodeAdapter.fromActionNode(action)]);
    };

    const onActionsHeaderButtonClick = (key: string) => {
        switch (key) {
            case 'add':
                saveAction(null);
                break;
            case 'edit':
                if (selectedIds.length !== 1) return;
                const selectedAction = findNodeById(selectedIds[0]);
                saveAction(selectedAction!);
                break;
            case 'remove':
                removeNodes(selectedIds);
                setSelectedIds([]);
                selectedIds.map((id) => {
                    const node = findNodeById(id);
                    if (node) {
                        removeAction(node.attributeID, node.optionID, node.id);
                    }
                });
                break;
            case 'play-selected':
                if (selectedIds.length === 0) return;
                selectedIds.forEach((id) => {
                    const node = findNodeById(id);
                    if (node) {
                        playAction(node);
                    }
                });
                break;
            case 'play':
                if (actions.length === 0) return;
                filteredData.forEach((node) => {
                    playAction(node);
                });
                break;
            default:
                console.warn(`Unknown action: ${key}`);
        }
    };

    const buttons = [
        { icon: <Plus />, name: 'add', tooltip: T._('Add Action', 'Admin'), onClick: () => onActionsHeaderButtonClick('add'), disabled: multiple },
        {
            icon: <Edit />,
            name: 'edit',
            tooltip: T._('Edit Actions', 'Admin'),
            onClick: () => onActionsHeaderButtonClick('edit'),
            disabled: selectedIds.length !== 1 || multiple
        },
        {
            icon: <Trash />,
            name: 'remove',
            tooltip: T._('Remove Action', 'Admin'),
            onClick: () => onActionsHeaderButtonClick('remove'),
            disabled: selectedIds.length === 0
        },
        {
            icon: <Play />,
            name: 'play-selected',
            tooltip: T._('Execute all the selected actions', 'Admin'),
            onClick: () => onActionsHeaderButtonClick('play-selected'),
            disabled: selectedIds.length === 0 || multiple
        },
        {
            icon: <Play />,
            name: 'play',
            tooltip: T._('Execute all the Actions', 'Admin'),
            onClick: () => onActionsHeaderButtonClick('play'),

            disabled: actions.length === 0 || multiple
        }
    ];

    const tabs = [
        { key: 'actions', label: T._('Actions', 'Admin') },
        { key: 'properties', label: T._('Properties', 'Admin') }
    ];
    if (ecommerceSettings?.ecommercePlatform === ComposerEcommercePlatform.WooCommerce) tabs.push({ key: 'price', label: T._('Price', 'Admin') });
    const [activeTab, setActiveTab] = React.useState<string>(tabs[0].key);
    const isActionsTab = activeTab === tabs[0].key;
    const tabSelector = (choice1: any, choice2: any, choice3: any): any => {
        return activeTab === tabs[0].key ? choice1 : activeTab === tabs[1].key ? choice2 : choice3;
    };
    const hasActions = filteredData.length > 0;

    const nodeSelectHandler = (e: React.MouseEvent, node: ComposerOptionActionNode) => {
        const ctrlKey = e.ctrlKey || e.metaKey;
        const shiftKey = e.shiftKey;

        // Se c'è il tasto shift, seleziono un intervallo di nodi
        if (shiftKey) {
            const lastSelectedId = selectedIds[selectedIds.length - 1];
            const lastSelectedNode = findNodeById(lastSelectedId);
            if (!lastSelectedNode) return;

            const startIndex = filteredData.findIndex((n) => n.id === lastSelectedId);
            const endIndex = filteredData.findIndex((n) => n.id === node.id);

            if (startIndex === -1 || endIndex === -1) return;

            const range = filteredData.slice(Math.min(startIndex, endIndex), Math.max(startIndex, endIndex) + 1);
            setSelectedIds(range.map((n) => n.id));
            return;
        }

        // con control key, aggiungo o rimuovo l'elemento selezionato
        if (ctrlKey && selectedIds.includes(node.id)) {
            setSelectedIds(selectedIds.filter((id) => id !== node.id));
            return;
        } else if (ctrlKey) {
            setSelectedIds((prev) => [...prev, node.id]);
            return;
        }
        // se non c'è control key, seleziono solo l'elemento corrente
        setSelectedIds([node.id]);
    };

    const moveNodeHandler = (node: ComposerOptionActionNode, direction: 'up' | 'down') => {
        const siblings = filteredData.sort((a, b) => sortByDisplay(filteredData, a, b));

        const currentIndex = siblings.findIndex((n) => n.id === node.id);
        const swapIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
        if (swapIndex >= 0 && swapIndex < siblings.length) {
            const swapNode = siblings[swapIndex];
            const currentOrder = node.displayOrder;

            updateNode(node.id, { ...node, displayOrder: swapNode.displayOrder });
            updateNode(swapNode.id, { ...swapNode, displayOrder: currentOrder });
            updateAction({ ...node, displayOrder: swapNode.displayOrder });
            updateAction({ ...swapNode, displayOrder: currentOrder });
        }
    };

    const dragStartHandler = (e: React.DragEvent<HTMLDivElement>, node: ComposerOptionActionNode) => {
        e.dataTransfer.setData('text/plain', node.id);
        e.dataTransfer.effectAllowed = 'move';
    };

    const dragOverHandler = (e: React.DragEvent<HTMLDivElement>, node: ComposerOptionActionNode) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
    };

    const dropHandler = (e: React.DragEvent<HTMLDivElement>, dropTargetNode: ComposerOptionActionNode | null) => {
        e.preventDefault();
        const draggedNodeId = e.dataTransfer.getData('text/plain');
        const draggedNode = findNodeById(draggedNodeId);

        if (!draggedNode) return;

        if (!dropTargetNode) return;
        const siblings = filteredData.sort((a, b) => sortByDisplay(filteredData, a, b));

        const dropIndex = siblings.findIndex((n) => n.id === dropTargetNode.id);

        // Update dragged node
        updateNode(draggedNode.id, { ...draggedNode, displayOrder: dropIndex });
        updateAction({ ...draggedNode, displayOrder: dropIndex });

        // Update siblings in new parent
        for (let i = dropIndex; i < siblings.length; i++) {
            const sibling = siblings[i];
            updateNode(sibling.id, { ...sibling, displayOrder: i + 1 });
            updateAction({ ...sibling, displayOrder: i + 1 });
        }
    };

    if (multiple) {
        return (
            <ComposerActionsContainer>
                <div>{T._('No action selected.', 'Admin')}</div>
            </ComposerActionsContainer>
        );
    }

    const nodes = filteredData.sort((a, b) => sortByDisplay(filteredData, a, b));

    const content = () => {
        return tabSelector(
            <ComposerActionsList $hasActions={hasActions}>
                {hasActions && (
                    <ComposerActionsSelector
                        selected={selectedIds.length === filteredData.length}
                        onClick={() => {
                            if (selectedIds.length === filteredData.length) {
                                setSelectedIds([]);
                            } else {
                                setSelectedIds(filteredData.map((n) => n.id));
                            }
                        }}
                    />
                )}

                {!hasActions && <div>No actions found</div>}
                {nodes.map((action) => (
                    <ComposerActionTreeElement
                        key={action.id}
                        action={action}
                        selected={selectedIds.includes(action.id)}
                        onSelect={nodeSelectHandler}
                        findMesh={(meshId) => (meshes ?? []).find((mesh) => mesh.id === meshId)}
                        findMaterial={(materialId) => (materials ?? []).find((material) => material.id === materialId)}
                        findCamera={(cameraId) => (cameras ?? []).find((camera) => camera.id === cameraId)}
                        onPlay={(a) => {
                            setSelectedIds([a.id]);
                            playAction(a);
                        }}
                        onDoubleClick={(_, action) => {
                            saveAction(action);
                        }}
                        onMoveDown={() => moveNodeHandler(action, 'down')}
                        onMoveUp={() => moveNodeHandler(action, 'up')}
                        onDragOver={(e) => dragOverHandler(e, action)}
                        onDragStart={(e) => dragStartHandler(e, action)}
                        onDrop={(e) => dropHandler(e, action)}
                    />
                ))}
            </ComposerActionsList>,
            <></>,
            <></>
        );
    };

    return (
        <ComposerActionsContainer>
            <ComposerActionsHeader>
                <TabHeader items={tabs} selectedItemKey={activeTab} onTabSelected={(item) => setActiveTab(item.key)} />
                {isActionsTab && <ToolGrid tools={buttons} columns={5} />}
            </ComposerActionsHeader>
            <ComposerActionsBody>
                {hasActions && activeTab === 'actions' && (
                    <>
                        <ComposerActionHeaders>
                            <div>Action</div>
                            <div>Selector</div>
                            <div>Value</div>
                        </ComposerActionHeaders>
                    </>
                )}
                {content()}
            </ComposerActionsBody>
        </ComposerActionsContainer>
    );
};

export default ComposerActions;
